import { useState, useCallback, useEffect } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import makeStyles from '@mui/styles/makeStyles'
import { motion } from 'framer-motion'
import debounce from 'lodash/debounce'
import { useHistory } from 'react-router-dom'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { Paper, TableCellTooltip } from '@components'
import { INNER_PAGE_HEIGHT } from '@components/layout'
import { NoData } from '@components/noData'
import { CvsTable } from '@components/table/cvs-table'
import { useQueryString } from '@global/hooks'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { getCellContent } from 'functions'
import { Toolbar } from './toolbar'
import { trackEvent, EventTrackingEventType } from 'lib/eventTracking'
import { t } from '@lingui/core/macro'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    height: '100%',
  },
  paper: {
    width: '100%',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  textField: {
    width: 200,
  },
}))

const getColumns = DATASETS_ENABLED => [
  {
    label: DATASETS_ENABLED ? t`Dataset Name` : t`Template Name`,
    type: 'text',
    width: '19%',
    lookup: 'template_name',
  },
  { label: t`Submission Name`, type: 'text', width: '19%', lookup: 'identifier' },
  { label: t`Submitted`, type: 'date', width: '9%', lookup: 'created_at' },
  { label: t`Submitted By`, type: 'text', width: '15%', lookup: 'submitted_by' },
  { label: t`Field`, type: 'date', width: '19%', lookup: 'field_name' },
  { label: t`Value`, width: '19%', lookup: 'cell_value', type: 'text' },
]

const Loader = () => (
  <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
    <CircularProgress color="primary" />
  </div>
)

export const Search = () => {
  const history = useHistory()
  const classes = useStyles()
  const [state, setState] = useState({
    loading: false,
    results: [],
    term: '',
  })
  const { term: searchTerm, loading, results } = state
  const [DATASETS_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE]),
  )
  const { setUrl, setStateFromQuery } = useQueryString(searchTerm, params => {
    setState(prevState => ({ ...prevState, term: params }))
  })

  useEffect(() => {
    if (window.location.search) {
      setStateFromQuery(term => {
        setState(prevState => ({
          ...prevState,
          loading: true,
          term,
        }))
        search(term)
      })
    }
  }, [])

  const search = term => {
    trackEvent(EventTrackingEventType.SUBMISSION_SEARCH, { term })
    api.post(`/api/core/v1/search/template-data/legacy`, { term }).then(({ data }) => {
      setState(prevState => ({
        ...prevState,
        loading: false,
        results: data.results.map((result, index) => ({ ...result, index })),
      }))
    })
  }

  const debouncedSearch = useCallback(
    debounce(e => {
      const term = e.target.value
      if (!term) {
        setState(prevState => ({
          ...prevState,
          loading: false,
          term: '',
          results: [],
        }))
        setUrl()
        return
      }
      setState(prevState => ({
        ...prevState,
        loading: true,
        term,
      }))
      setUrl({ q: term })
      search(term)
    }, 500),
    [],
  )

  const noDatasetsFoundInSearchResults = !state.loading && searchTerm && state.results.length === 0

  let noDataMainText
  if (DATASETS_ENABLED) {
    noDataMainText = t`Quickly track down which submission and dataset contain the desired data`
  } else {
    noDataMainText = t`Quickly track down which submission and template contain the desired data`
  }

  return (
    <motion.div
      className={classes.root}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
    >
      <Paper className={classes.paper}>
        <Toolbar onChange={debouncedSearch} defaultValue={searchTerm} />
        <div style={{ flex: 1, height: INNER_PAGE_HEIGHT }}>
          <div
            style={{
              maxHeight: '100%',
              height: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ flex: 1, height: '100%' }}>
              {!searchTerm ? (
                <NoData
                  text={{
                    title: t`Search Successfully Submitted Values`,
                    body: {
                      mainText: noDataMainText,
                    },
                  }}
                  imageFileName="undraw_taking_notes.svg"
                />
              ) : null}
              {searchTerm && loading && <Loader />}
              {noDatasetsFoundInSearchResults ? (
                <NoData
                  text={{
                    title: t`No datasets found`,
                    body: {
                      mainText: t`We couldn't find any datasets matching your search.`,
                      helperText: {
                        text: t`Please try another search.`,
                      },
                    },
                  }}
                  imageFileName="undraw_taking_notes.svg"
                />
              ) : null}
              {searchTerm && !loading && (
                <CvsTable
                  columns={getColumns(DATASETS_ENABLED)}
                  rows={results}
                  orderBy="created_at"
                  defaultSort="asc"
                  sortOrder="desc"
                  keyField="index"
                  noAdd={true}
                  noPagination={true}
                  term={searchTerm}
                  style={{ maxWidth: '100%', paddingBottom: '50px' }}
                  headerRowPadding={50}
                >
                  {({ row }) => {
                    return (
                      <TableRow
                        hover
                        onClick={_id => history.push(`/codes/details/${row.dataset_id}/data`)}
                        tabIndex={-1}
                        key={row.dataset_id}
                        style={{ cursor: 'pointer', maxWidth: '100%', overflow: 'hidden' }}
                      >
                        <TableCell
                          component="th"
                          scope="row"
                          padding="normal"
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            paddingLeft: 50,
                          }}
                        >
                          {row.template_name}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          padding="normal"
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row.identifier}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          padding="normal"
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {getCellContent('date', row.created_at)}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          padding="normal"
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row.submitted_by}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          padding="normal"
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row.field_name}
                        </TableCell>
                        <TableCellTooltip title={row.cell_value}>
                          <TableCell
                            component="th"
                            scope="row"
                            padding="normal"
                            style={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {row.cell_value}
                          </TableCell>
                        </TableCellTooltip>
                      </TableRow>
                    )
                  }}
                </CvsTable>
              )}
            </div>
          </div>
        </div>
      </Paper>
    </motion.div>
  )
}
